import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { accountLabel, expenseCostType, type BudgetLine, type Expense, type ExpenseAccount, type Income, type Platform } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import {
  apiErrorMessage,
  deleteExpense,
  fetchBudgets,
  fetchExpense,
  fetchExpenses,
  fetchIncomes,
  fetchPlatforms,
  fetchSummary,
  type ExpenseRow,
  type SummaryResponse,
} from "../services/expensesService";
import { fetchAccounts } from "../services/statementsService";
import ExpenseDialog from "../components/ExpenseDialog";
import ExpenseCategoryCharts from "../components/ExpenseCategoryCharts";
import LossesCard from "../components/LossesCard";
import ClientScopeSelect from "../../work-hours/components/ClientScopeSelect";
import { Chip, ErrorBox, Kpi, Notice, Page, Spinner } from "../components/ui";
import { expensesCsv } from "../lib/budget";
import { CATEGORY_COLORS, categoryLabel, categoryOptions, currentDay, currentMonth, downloadText, lastDayOf, money, monthLabel, shiftMonth } from "../lib/format";
import { reimbursableChip } from "../lib/reimbursable";
import { INVOICE_TONES, ui } from "../theme";

// Registro de gastos (#608): filtros por mes, categoría, factura y estado; alta,
// edición, archivos y exportación. La nómina pagada aparece como solo lectura.
// Arriba de la lista, las gráficas por categoría y por mes de lo filtrado.
// `?expense=<id>` abre ese gasto (el enlace «Ver gasto» de Activos, #1638).

export default function ExpensesSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);

  // Abre en «Todos los meses»: el mes en curso suele tener pocos cargos porque
  // el estado de cuenta se carga después del corte, y parecía que no había gastos.
  const [month, setMonth] = useState("all");
  const [category, setCategory] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [includePayroll, setIncludePayroll] = useState(true);
  // Reembolso: todos, reembolsables, no reembolsables o sin decidir.
  const [reimbursable, setReimbursable] = useState("");
  // Tipo de costo: todos, fijos o variables.
  const [costType, setCostType] = useState("");
  // Solo los gastos marcados como pérdida (#1177).
  const [onlyLosses, setOnlyLosses] = useState(false);
  const [lossReload, setLossReload] = useState(0);
  // #980: todos los clientes, uno, o solo lo propio (sin cliente).
  const [clientId, setClientId] = useState("");
  // Cuenta bancaria o tarjeta (#952).
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState<ExpenseAccount[]>([]);
  const [rows, setRows] = useState<ExpenseRow[]>([]);
  const [payrollSkipped, setPayrollSkipped] = useState(0);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  // Para las gráficas: los presupuestos alimentan la proyección y los ingresos, la vista «Ingresos vs egresos».
  // `null` mientras no llegan (o si fallan): sin presupuestos a la mano no se proyecta.
  const [budgets, setBudgets] = useState<BudgetLine[] | null>(null);
  const [incomes, setIncomes] = useState<Income[]>([]);
  // #1178: nómina estimada del padrón (la misma que suman Resumen y Presupuestos).
  // Solo entra a las gráficas, no a la tabla: no es un gasto registrado.
  const [rosterRows, setRosterRows] = useState<SummaryResponse["expenses"]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ open: boolean; expense: Expense | null }>({ open: false, expense: null });

  // Enlace directo a un gasto: se abre una vez y el parámetro se quita de la URL.
  // (Quitar el parámetro vuelve a correr el efecto: la vida del pedido va con el montaje.)
  const [params, setParams] = useSearchParams();
  const linkedExpense = params.get("expense");
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!linkedExpense) return;
    fetchExpense(linkedExpense)
      .then((e) => mounted.current && e && setDialog({ open: true, expense: e }))
      .catch(() => mounted.current && setError("load"));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("expense");
        return next;
      },
      { replace: true },
    );
  }, [linkedExpense, setParams]);

  const range =useMemo(() => (month === "all" ? {} : { from: `${month}-01`, to: lastDayOf(month) }), [month]);

  // Solo la respuesta de la última petición pinta la tabla: si cambias el mes
  // antes de que llegue la anterior, la vieja no pisa a la nueva.
  const requestId = useRef(0);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const r = await fetchExpenses({
        ...range,
        invoiceStatus: invoiceStatus || undefined,
        status: status || undefined,
        clientId: clientId || undefined,
        accountId: accountId || undefined,
        reimbursable: reimbursable || undefined,
        costType: costType || undefined,
        loss: onlyLosses ? "true" : undefined,
        includePayroll,
      });
      if (id !== requestId.current) return;
      setRows(r.expenses);
      setPayrollSkipped(r.payrollSkipped);
      setError(null);
    } catch (err) {
      if (id === requestId.current) setError(apiErrorMessage(err) || "load");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [range, invoiceStatus, status, clientId, reimbursable, costType, onlyLosses, accountId, includePayroll]);

  useEffect(() => {
    void load();
  }, [load]);

  // Los ingresos solo dependen del periodo: no se vuelven a pedir con cada filtro.
  // Son un extra de las gráficas: si fallan, la lista sigue.
  useEffect(() => {
    let alive = true;
    fetchIncomes(range)
      .then((list) => alive && setIncomes(Array.isArray(list) ? list : []))
      .catch(() => alive && setIncomes([]));
    return () => {
      alive = false;
    };
  }, [range]);

  useEffect(() => {
    let alive = true;
    const to = currentMonth();
    fetchSummary({ from: shiftMonth(to, -23), to })
      .then((s) => alive && setRosterRows((s?.expenses || []).filter((e) => e.source === "padron")))
      .catch(() => alive && setRosterRows([]));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    fetchPlatforms().then(setPlatforms).catch(() => setPlatforms([]));
    // Se piden cada vez que se abre la sección: la proyección sigue al presupuesto vigente.
    fetchBudgets()
      .then((b) => setBudgets(Array.isArray(b) ? b : []))
      .catch(() => setBudgets(null));
    fetchAccounts()
      .then((a) => setAccounts(Array.isArray(a) ? a : []))
      .catch(() => setAccounts([]));
  }, []);

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.vendor} ${r.description} ${r.justification || ""} ${r.cardLast4 || ""}`.toLowerCase().includes(q));
  }, [rows, search]);
  // La categoría se filtra aquí y no en la API: las gráficas necesitan las demás
  // categorías para comparar la elegida contra el resto.
  const visible = useMemo(() => (category ? searched.filter((r) => r.category === category) : searched), [searched, category]);

  // La proyección sale de los presupuestos, que son por categoría y por mes: solo
  // tiene sentido con todos los meses a la vista y sin filtros que un presupuesto no sigue.
  // Sin la nómina en la lista tampoco: su presupuesto se proyectaría sobre barras que no la traen.
  const canProject = month === "all" && includePayroll && !invoiceStatus && !status && !clientId && !accountId && !reimbursable && !search.trim();

  // La nómina del padrón entra a las gráficas con las mismas condiciones en que la
  // suma el Resumen: sin cliente ni filtros que un padrón no tiene (factura, cuenta…).
  const withRoster = includePayroll && !clientId && !accountId && !invoiceStatus && !status && !reimbursable && !search.trim() && costType !== "variable";
  const chartRows = useMemo(() => {
    if (!withRoster || !rosterRows.length) return searched;
    const inMonth = month === "all" ? rosterRows : rosterRows.filter((e) => e.period === month);
    return [...searched, ...inMonth.map((e) => ({ ...e, costType: null, personal: false }))];
  }, [searched, rosterRows, withRoster, month]);

  const total = visible.reduce((s, r) => s + (r.amountBase || 0), 0);
  const pendingInvoices = visible.filter((r) => r.invoiceStatus === "pendiente" && !r.readOnly).length;
  const toReview = visible.filter((r) => r.status === "por_revisar").length;
  const withoutRate = visible.filter((r) => r.amountBase == null).length;
  const platformName = (id?: string | null) => platforms.find((p) => p.id === id)?.name;

  const onDelete = async (r: ExpenseRow) => {
    if (!window.confirm(t("expenses.list.confirmDelete", { vendor: r.vendor }))) return;
    try {
      await deleteExpense(r.id);
      // Si era una pérdida, la tarjeta tiene que dejar de contarla.
      setLossReload((n) => n + 1);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err) || "delete");
    }
  };

  const months = Array.from({ length: 24 }, (_, i) => shiftMonth(currentMonth(), -i));
  const errorText = error === "load" ? t("expenses.errors.load") : error === "delete" ? t("expenses.errors.delete") : error;

  return (
    <Page
      title={t("expenses.menu.expenses")}
      subtitle={t("expenses.list.subtitle")}
      actions={
        <>
          <button type="button" className={c.secondary} onClick={() => downloadText(`gastos-${month}.csv`, expensesCsv(visible, i18n.language))} disabled={!visible.length}>
            <i className="ri-download-2-line mr-1" />
            CSV
          </button>
          <button type="button" className={c.primary} onClick={() => setDialog({ open: true, expense: null })}>
            <i className="ri-add-line mr-1" />
            {t("expenses.list.new")}
          </button>
        </>
      }
    >
      <ErrorBox message={errorText} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label={t("expenses.kpi.total")} value={money(total, "MXN", i18n.language)} hint={month === "all" ? t("expenses.list.allMonths") : monthLabel(month, i18n.language)} />
        <Kpi label={t("expenses.kpi.count")} value={String(visible.length)} />
        <Kpi label={t("expenses.kpi.withoutInvoice")} value={String(pendingInvoices)} tone={pendingInvoices ? "bad" : "good"} />
        <Kpi label={t("expenses.kpi.toReview")} value={String(toReview)} tone={toReview ? "bad" : undefined} />
      </div>

      <div className={`${c.card} grid grid-cols-2 gap-2 md:grid-cols-6`}>
        <select aria-label={t("expenses.filters.month")} className={c.input} value={month} onChange={(e) => setMonth(e.target.value)}>
          <option value="all">{t("expenses.list.allMonths")}</option>
          {months.map((m) => (
            <option key={m} value={m}>
              {monthLabel(m, i18n.language)}
            </option>
          ))}
        </select>
        <select aria-label={t("expenses.filters.category")} className={c.input} value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">{t("expenses.filters.allCategories")}</option>
          {categoryOptions(i18n.language, { archived: true }).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select aria-label={t("expenses.filters.invoice")} className={c.input} value={invoiceStatus} onChange={(e) => setInvoiceStatus(e.target.value)}>
          <option value="">{t("expenses.filters.anyInvoice")}</option>
          {["pendiente", "adjunta", "no_aplica"].map((s) => (
            <option key={s} value={s}>
              {t(`expenses.invoice.${s}`)}
            </option>
          ))}
        </select>
        <select aria-label={t("expenses.filters.status")} className={c.input} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t("expenses.filters.anyStatus")}</option>
          <option value="confirmado">{t("expenses.status.confirmado")}</option>
          <option value="por_revisar">{t("expenses.status.por_revisar")}</option>
        </select>
        <select aria-label={t("expenses.filters.reimbursable")} className={c.input} value={reimbursable} onChange={(e) => setReimbursable(e.target.value)}>
          <option value="">{t("expenses.filters.anyReimbursable")}</option>
          <option value="true">{t("expenses.attribution.reimbursableShort")}</option>
          <option value="false">{t("expenses.attribution.notReimbursableShort")}</option>
          <option value="pending">{t("expenses.attribution.pendingShort")}</option>
        </select>
        <select aria-label={t("expenses.filters.costType")} className={c.input} value={costType} onChange={(e) => setCostType(e.target.value)}>
          <option value="">{t("expenses.filters.anyCostType")}</option>
          <option value="fijo">{t("expenses.costType.fijo")}</option>
          <option value="variable">{t("expenses.costType.variable")}</option>
        </select>
        <ClientScopeSelect value={clientId} onChange={setClientId} includeNone className={c.input} />
        {accounts.length > 0 && (
          <select aria-label={t("expensesStatements:common.account")} className={c.input} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">{t("expensesStatements:common.allAccounts")}</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {accountLabel(a)}
              </option>
            ))}
          </select>
        )}
        <input aria-label={t("expenses.filters.search")} className={c.input} placeholder={t("expenses.filters.search")} value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className={`flex items-center gap-2 text-sm ${c.muted}`}>
          <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={includePayroll} onChange={(e) => setIncludePayroll(e.target.checked)} />
          {t("expenses.filters.includePayroll")}
        </label>
        <label className={`flex items-center gap-2 text-sm ${c.muted}`}>
          <input type="checkbox" className="h-4 w-4 accent-red-600" data-testid="only-losses" checked={onlyLosses} onChange={(e) => setOnlyLosses(e.target.checked)} />
          {t("expenses.losses.onlyLosses")}
        </label>
      </div>

      {/* Pérdidas del periodo (#1177): nómina sin horas justificadas + gastos marcados. */}
      <LossesCard
        from={month === "all" ? shiftMonth(currentMonth(), -11) : month}
        to={month === "all" ? currentMonth() : month}
        reload={lossReload}
      />

      {/* Siempre montadas: si se desmontaran al recargar se perdería la vista elegida y el foco. */}
      <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"} aria-busy={loading}>
        <ExpenseCategoryCharts
          rows={chartRows}
          category={category}
          onSelectCategory={setCategory}
          budgets={canProject && budgets ? budgets : undefined}
          incomes={incomes}
          today={currentDay()}
          costType={costType}
        />
      </div>

      {withoutRate > 0 && <Notice tone="warn">{t("expenses.list.withoutRate", { count: withoutRate })}</Notice>}
      {payrollSkipped > 0 && includePayroll && <Notice tone="warn">{t("expenses.list.payrollSkipped", { count: payrollSkipped })}</Notice>}

      <div className={`${c.card} overflow-x-auto p-0`}>
        {loading ? (
          <div className="p-4">
            <Spinner />
          </div>
        ) : (
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className={`text-left text-xs ${c.muted}`}>
                <th className="px-4 py-2">{t("expenses.fields.date")}</th>
                <th className="px-2 py-2">{t("expenses.fields.vendor")}</th>
                <th className="px-2 py-2">{t("expenses.fields.category")}</th>
                <th className="px-2 py-2">{t("expenses.fields.payment")}</th>
                <th className="px-2 py-2 text-right">{t("expenses.fields.amount")}</th>
                <th className="px-2 py-2">{t("expenses.fields.invoice")}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} className={`border-t ${c.border} ${c.text} ${c.rowHover}`} data-testid={`expense-${r.id}`}>
                  <td className="whitespace-nowrap px-4 py-2 tabular-nums">{r.date}</td>
                  <td className="px-2 py-2">
                    <p className="font-medium">
                      {r.vendor}
                      {r.status === "por_revisar" && <Chip className="ml-2 bg-amber-500/15 text-amber-600">{t("expenses.status.por_revisar")}</Chip>}
                      {reimbursableChip(r) === "reimbursable" && <Chip className="ml-2 bg-emerald-500/15 text-emerald-600">{t("expenses.attribution.reimbursableShort")}</Chip>}
                      {reimbursableChip(r) === "not_reimbursable" && <Chip className="ml-2 bg-slate-500/15 text-slate-500">{t("expenses.attribution.notReimbursableShort")}</Chip>}
                      {reimbursableChip(r) === "pending" && <Chip className="ml-2 bg-amber-500/15 text-amber-600">{t("expenses.attribution.pendingShort")}</Chip>}
                      {reimbursableChip(r) === "personal" && <Chip className="ml-2 bg-slate-500/15 text-slate-500">{t("expenses.attribution.personalShort")}</Chip>}
                    </p>
                    <p className={`max-w-md truncate text-xs ${c.muted}`} title={[r.description, r.justification].filter(Boolean).join(" — ")}>
                      {r.description || "—"}
                      {platformName(r.platformId) && <span className="ml-1 text-violet-500">· {platformName(r.platformId)}</span>}
                    </p>
                  </td>
                  <td className="px-2 py-2">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className="h-2 w-2 rounded-full" style={{ background: CATEGORY_COLORS[r.category] }} />
                      {categoryLabel(r.category, i18n.language)}
                    </span>
                    <p className={`mt-0.5 text-xs ${c.muted}`} data-testid={`cost-type-${r.id}`}>
                      {t(`expenses.costType.${expenseCostType(r)}Short`)}
                    </p>
                    {r.loss === true && (
                      <span className="mt-0.5 inline-block rounded bg-red-600 px-1.5 py-0.5 text-[10px] text-white" data-testid={`loss-${r.id}`}>
                        {t("expenses.losses.tag")}
                      </span>
                    )}
                  </td>
                  <td className={`px-2 py-2 text-xs ${c.muted}`}>
                    {t(`expenses.payment.${r.paymentMethod}`)}
                    {r.cardLast4 ? ` ·${r.cardLast4}` : ""}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                    <p className="font-semibold">{money(r.amount, r.currency, i18n.language)}</p>
                    {r.currency !== "MXN" &&
                      (r.amountBase == null ? (
                        <Chip className="bg-amber-500/15 text-amber-600">{t("expenses.list.noRate")}</Chip>
                      ) : (
                        <p className={`text-xs ${c.muted}`}>{money(r.amountBase, "MXN", i18n.language)}</p>
                      ))}
                  </td>
                  <td className="px-2 py-2">
                    <Chip className={INVOICE_TONES[r.invoiceStatus]}>{t(`expenses.invoice.${r.invoiceStatus}`)}</Chip>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    {r.readOnly ? (
                      <span className={`text-xs ${c.muted}`} title={t("expenses.list.fromPayroll")}>
                        <i className="ri-lock-line mr-1" />
                        {t(`expenses.source.${r.source}`)}
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          aria-label={`edit-${r.id}`}
                          title={(r.attachments || []).length ? t("expenses.list.filesCount", { count: r.attachments.length }) : t("expenses.list.edit")}
                          className="mr-3 text-teal-600"
                          onClick={() => setDialog({ open: true, expense: r })}
                        >
                          <i className="ri-edit-line" />
                          {(r.attachments || []).length > 0 && (
                            <span className="ml-0.5 text-xs">
                              <i className="ri-attachment-2" />
                              {r.attachments.length}
                            </span>
                          )}
                        </button>
                        <button type="button" aria-label={`delete-${r.id}`} className="text-red-500" onClick={() => onDelete(r)}>
                          <i className="ri-delete-bin-line" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={7} className={`px-4 py-8 text-center text-sm ${c.muted}`}>
                    {t("expenses.list.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <ExpenseDialog
        open={dialog.open}
        expense={dialog.expense}
        platforms={platforms}
        accounts={accounts}
        onClose={() => setDialog({ open: false, expense: null })}
        onSaved={() => {
          void load();
          setLossReload((n) => n + 1);
        }}
      />
    </Page>
  );
}
